import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

/**
 * A reusable data table component with searching and pagination
 */
const DataTable = ({
  title,
  data,
  columns,
  initialRowsPerPage = 10,
  searchEnabled = true,
  searchPlaceholder = "Rechercher...",
  emptyMessage = "Aucune donnée à afficher",
  tableProps = {},
  paperProps = {},
}) => {
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Filter data based on search query
  const filteredData =
    searchQuery && searchEnabled
      ? data.filter((row) =>
          // Search in all columns that are searchable
          columns.some(
            (column) =>
              column.searchable !== false &&
              row[column.field]
                ?.toString()
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
          )
        )
      : data;

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search query changes
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  // Empty rows to maintain consistent height
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: "12px",
        border: "1px solid #f0f0f0",
        ...paperProps.sx,
      }}
      {...paperProps}
    >
      {title && (
        <Typography
          variant="h6"
          sx={{
            p: 2,
            fontWeight: "bold",
            color: "#444349",
          }}
        >
          {title}
        </Typography>
      )}

      {searchEnabled && (
        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: { borderRadius: "8px" },
            }}
          />
        </Box>
      )}

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table
          stickyHeader
          aria-label="sticky table"
          size="medium"
          {...tableProps}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || "left"}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#f9f9f9",
                    color: "#444349",
                  }}
                >
                  {column.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length > 0 ? (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id || index}
                      sx={{
                        "&:nth-of-type(even)": { backgroundColor: "#fafafa" },
                      }}
                    >
                      {columns.map((column) => {
                        const value = row[column.field];
                        return (
                          <TableCell
                            key={column.field}
                            align={column.align || "left"}
                          >
                            {column.renderCell ? column.renderCell(row) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
            ) : (
              <TableRow style={{ height: 53 * Math.min(rowsPerPage, 5) }}>
                <TableCell colSpan={columns.length} align="center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={columns.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Lignes par page:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} sur ${count}`
        }
        sx={{
          borderTop: "1px solid #eee",
          "& .MuiTablePagination-select": {
            marginRight: "8px",
          },
        }}
      />
    </Paper>
  );
};

export default DataTable;
